// Purchase receipt (/purchases/:id) — GET /orders/:id rendered as a printable
// kwitansi: frozen price breakdown (list vs diskon), QRIS fee, billed total,
// provider txn ref, and the account email. "Cetak / simpan PDF" = window.print();
// the @media print rules in styles.css strip the app chrome and every button, so
// only the receipt prints (ink on paper). Non-paid orders render the same detail with
// the honest status; a pending one links back into checkout with a quiet text link, like
// /purchases. Nothing here says anything about WHEN the order was placed relative to a
// sales switch — a receipt must never assert something about itself that isn't in the row.
// Structure: markmead/hyperui details-list (DescriptionList) inside
// shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT.

import { Link, useParams } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Logo,
} from "@/ui";
import { Loading, ErrorState } from "@/components/States";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { useAuth } from "@/lib/auth";
import {
  getOrder,
  orderDisplayStatus,
  orderHasPaymentDetails,
  orderPriceBreakdown,
} from "@/lib/orders";
import { MARKETING_BASE } from "@/lib/config";
import {
  formatDateTime,
  formatRupiah,
  orderStatusLabel,
  orderStatusTone,
  tierLabel,
} from "@/lib/format";
import type { Order } from "@/lib/types";
import { DetailHeader } from "@/components/DetailHeader";

/** Title of a non-paid order, and of the screen before the order is known. */
const DETAIL_TITLE = "Detail pesanan";

/** Price rows read as a receipt: label left, amount right, at every width. */
const priceTermClass = "mt-0";
const priceValueClass = cn(dataClass, "text-right text-fg-0");

function Missing() {
  return <span className="text-fg-2">—</span>;
}

export function PurchaseDetail() {
  const { id = "" } = useParams();
  const { user } = useAuth();

  const { data: order, loading, error, reload } = useAsync<Order>((s) => getOrder(id, s), [id]);

  // The h1 renders in every state (ui/DESIGN.md §10).
  const placeholderHeader = (
    <DetailHeader backTo="/purchases" backLabel="Semua pembelian" title={DETAIL_TITLE} />
  );
  if (loading && !order) {
    return (
      <>
        {placeholderHeader}
        <Loading label="Memuat pesanan…" />
      </>
    );
  }
  if (error || !order) {
    return (
      <>
        {placeholderHeader}
        <ErrorState error={error ?? new Error("Pesanan tidak ditemukan.")} onRetry={reload} />
      </>
    );
  }

  const o = order;
  // Same pre-flip honesty as /purchases: a pending order past its QRIS deadline
  // shows Kedaluwarsa even before the server maintenance loop flips the row.
  const shownStatus = orderDisplayStatus(o);
  const paid = o.status === "paid";
  const price = orderPriceBreakdown(o);

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      {/* Issuer — print only (the sidebar Logo is hidden in print): the brand Logo
          plus the site domain. The h1 below already says "Kwitansi". */}
      <p className="hidden items-center gap-3 text-sm text-fg-2 print:flex">
        <Logo />
        <span>{MARKETING_BASE.replace(/^https?:\/\//, "")}</span>
      </p>

      <DetailHeader
        backTo="/purchases"
        backLabel="Semua pembelian"
        title={paid ? "Kwitansi" : DETAIL_TITLE}
        actions={
          paid ? (
            <>
              <Button asChild variant="quiet" size="sm">
                <Link to="/license">Kelola lisensi</Link>
              </Button>
              <Button variant="secondary" size="sm" onClick={() => window.print()}>
                <Glyph name="printer" />
                Cetak / simpan PDF
              </Button>
            </>
          ) : undefined
        }
      />

      {/* Informational, so no live-region role. Only what the row itself says: it is
          unpaid, it can still be resumed, and leaving it costs nothing. */}
      {shownStatus === "pending" && (
        <Alert className="print:hidden">
          <div className="flex flex-col gap-2">
            <p>
              Pesanan ini belum dibayar.
              {orderHasPaymentDetails(o) &&
                " Kalau tidak dibayar, pesanan ini kedaluwarsa sendiri tanpa biaya."}
            </p>
            <p>
              <Link to={`/buy?order=${encodeURIComponent(o.id)}`}>Lanjutkan pembayaran</Link>
            </p>
          </div>
        </Alert>
      )}

      <Card>
        <DescriptionList>
          <DescriptionTerm>Status</DescriptionTerm>
          <DescriptionDetails>
            <Badge tone={orderStatusTone(shownStatus)} dot>
              {orderStatusLabel(shownStatus)}
            </Badge>
          </DescriptionDetails>
          <DescriptionTerm>Produk</DescriptionTerm>
          <DescriptionDetails>Lisensi ahcut {tierLabel(o.tier)}</DescriptionDetails>
          <DescriptionTerm>Order ID</DescriptionTerm>
          <DescriptionDetails className={dataClass}>{o.id}</DescriptionDetails>
          <DescriptionTerm>Dibuat</DescriptionTerm>
          <DescriptionDetails className={dataClass}>{formatDateTime(o.created_at)}</DescriptionDetails>
          <DescriptionTerm>Dibayar</DescriptionTerm>
          <DescriptionDetails className={o.paid_at ? dataClass : undefined}>
            {o.paid_at ? formatDateTime(o.paid_at) : <Missing />}
          </DescriptionDetails>
          <DescriptionTerm>Email akun</DescriptionTerm>
          <DescriptionDetails>{user?.email ?? <Missing />}</DescriptionDetails>
          <DescriptionTerm>Metode</DescriptionTerm>
          <DescriptionDetails>QRIS — Pakasir</DescriptionDetails>
          <DescriptionTerm>ID transaksi</DescriptionTerm>
          <DescriptionDetails className={o.provider_txn_id ? dataClass : undefined}>
            {o.provider_txn_id ?? <Missing />}
          </DescriptionDetails>
        </DescriptionList>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pembayaran</CardTitle>
        </CardHeader>
        <DescriptionList className="grid-cols-[minmax(0,1fr)_auto] gap-y-3 sm:grid-cols-[minmax(0,1fr)_auto]">
          {o.coupon_code ? (
            <>
              <DescriptionTerm className={priceTermClass}>Harga awal</DescriptionTerm>
              <DescriptionDetails className={priceValueClass}>{formatRupiah(price.list)}</DescriptionDetails>
              <DescriptionTerm className={priceTermClass}>Kupon</DescriptionTerm>
              <DescriptionDetails className={priceValueClass}>{o.coupon_code}</DescriptionDetails>
              <DescriptionTerm className={priceTermClass}>Diskon</DescriptionTerm>
              <DescriptionDetails className={priceValueClass}>
                −{formatRupiah(price.discount)}
              </DescriptionDetails>
              <DescriptionTerm className={priceTermClass}>Harga setelah diskon</DescriptionTerm>
              <DescriptionDetails className={priceValueClass}>{formatRupiah(o.amount)}</DescriptionDetails>
            </>
          ) : (
            <>
              <DescriptionTerm className={priceTermClass}>Harga</DescriptionTerm>
              <DescriptionDetails className={priceValueClass}>{formatRupiah(o.amount)}</DescriptionDetails>
            </>
          )}
          <DescriptionTerm className={priceTermClass}>Fee QRIS</DescriptionTerm>
          <DescriptionDetails className={priceValueClass}>
            {price.fee !== null ? formatRupiah(price.fee) : <Missing />}
          </DescriptionDetails>
        </DescriptionList>
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-t border-line-strong pt-4">
          <p className="font-medium text-fg-0">Total tertagih</p>
          <p className="flex items-baseline gap-2">
            <span className={cn(dataClass, "text-price text-fg-0")}>{formatRupiah(price.total)}</span>
            <span className="text-sm text-fg-2">{o.currency}</span>
          </p>
        </div>
      </Card>

      {paid && (
        <p className="max-w-reading text-sm text-fg-2">
          Kwitansi ini adalah bukti pembayaran resmi pesananmu. Lisensi bersifat permanen dan
          mencakup seluruh pembaruan (patch, minor, mayor) tanpa biaya tambahan dan tanpa pembelian
          ulang, selama ahcut masih merilis pembaruan; versi yang sudah dimiliki tetap berfungsi
          selamanya.
        </p>
      )}
    </>
  );
}
